/**
 * アバター管理画面
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAvatars, getAvatar, createAvatar, updateAvatar, deleteAvatar } from '../../lib/admin-api';
import { apiRequest } from '../../lib/queryClient';
import { useLocation } from 'wouter';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Search, Loader2, Eye, Stethoscope, ClipboardList } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

/** 管理画面のアバター行から /api/start-interview 用に性別・年代・IDを解決する */
function parseAvatarForInterview(avatar: {
  id: number;
  profile_data?: { gender?: string; age?: number | string };
}): { gender: '男性' | '女性'; ageRange: '20-34' | '35-49' | '50+'; avatarId: number } {
  const profile = avatar.profile_data || {};
  const gender = profile.gender;
  const age = typeof profile.age === 'number' ? profile.age : Number(profile.age);
  if (gender !== '男性' && gender !== '女性') {
    throw new Error('プロフィールに性別（男性/女性）が必要です');
  }
  if (!Number.isFinite(age)) {
    throw new Error('プロフィールに年齢が必要です');
  }
  if (age < 20) {
    throw new Error('面接は20歳以上のアバターです');
  }
  let ageRange: '20-34' | '35-49' | '50+';
  if (age >= 20 && age <= 34) ageRange = '20-34';
  else if (age >= 35 && age <= 49) ageRange = '35-49';
  else ageRange = '50+';
  return { gender, ageRange, avatarId: avatar.id };
}

export default function Avatars() {
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState<string>('');
  const [ageRangeFilter, setAgeRangeFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', profile_data: '{}', avatar_image_url: '' });
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location, setLocation] = useLocation();
  const processedIdRef = useRef<string | null>(null);

  const ageBounds = (() => {
    if (ageRangeFilter === '20-34') return { ageMin: 20, ageMax: 34 };
    if (ageRangeFilter === '35-49') return { ageMin: 35, ageMax: 49 };
    if (ageRangeFilter === '50+') return { ageMin: 50, ageMax: 200 };
    return { ageMin: undefined, ageMax: undefined };
  })();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'avatars', search, genderFilter, ageRangeFilter, page],
    queryFn: () => getAvatars({
      search,
      gender: genderFilter || undefined,
      ageMin: ageBounds.ageMin,
      ageMax: ageBounds.ageMax,
      page,
      limit: 20,
    }),
  });

  // URLパラメータからIDを読み取って編集ダイアログを開く
  useEffect(() => {
    const params = new URLSearchParams(location.split('?')[1]);
    const idParam = params.get('id');
    
    // 既に処理済みのIDの場合はスキップ
    if (!idParam || idParam === processedIdRef.current) {
      return;
    }
    
    if (!isLoading) {
      const avatarId = parseInt(idParam, 10);
      processedIdRef.current = idParam;
      
      // 現在のページのデータから検索
      const avatar = data?.avatars?.find((a: any) => a.id === avatarId);
      
      if (avatar) {
        handleEdit(avatar);
        // URLからパラメータを削除
        window.history.replaceState({}, '', '/admin/avatars');
        processedIdRef.current = null; // 処理完了後リセット
      } else if (data && !isLoading) {
        // 現在のページに見つからない場合は、APIから直接取得
        getAvatar(avatarId)
          .then((response) => {
            if (response.avatar) {
              handleEdit(response.avatar);
              // URLからパラメータを削除
              window.history.replaceState({}, '', '/admin/avatars');
            }
            processedIdRef.current = null; // 処理完了後リセット
          })
          .catch((error) => {
            console.error('アバター情報の取得に失敗しました:', error);
            processedIdRef.current = null; // エラー時もリセット
          });
      }
    }
  }, [location, data, isLoading]);

  const createMutation = useMutation({
    mutationFn: createAvatar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'avatars'] });
      setIsCreateDialogOpen(false);
      setFormData({ name: '', profile_data: '{}', avatar_image_url: '' });
      toast({
        title: '成功',
        description: 'アバターを作成しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'アバターの作成に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateAvatar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'avatars'] });
      setIsEditDialogOpen(false);
      setSelectedAvatar(null);
      toast({
        title: '成功',
        description: 'アバターを更新しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'アバターの更新に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const startInterviewMutation = useMutation({
    mutationFn: async ({ avatar, mode }: { avatar: any; mode: 'standard' | 'intake' }) => {
      const params = parseAvatarForInterview(avatar);
      const body =
        mode === 'intake'
          ? {
              mode: 'intake' as const,
              gender: params.gender,
              ageRange: params.ageRange,
              avatarId: params.avatarId,
            }
          : {
              gender: params.gender,
              ageRange: params.ageRange,
              avatarId: params.avatarId,
            };
      const res = await apiRequest('POST', '/api/start-interview', body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/avatar/current'] });
      setLocation('/interview');
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : '面接の開始に失敗しました';
      toast({
        title: 'エラー',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAvatar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'avatars'] });
      setIsDeleteDialogOpen(false);
      setSelectedAvatar(null);
      toast({
        title: '成功',
        description: 'アバターを削除しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'アバターの削除に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const handleCreate = () => {
    try {
      const profileData = JSON.parse(formData.profile_data);
      createMutation.mutate({ ...formData, profile_data: profileData });
    } catch (e) {
      toast({
        title: 'エラー',
        description: 'プロフィールデータの形式が正しくありません',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (avatar: any) => {
    setSelectedAvatar(avatar);
    setFormData({
      name: avatar.name || '',
      profile_data: typeof avatar.profile_data === 'object' 
        ? JSON.stringify(avatar.profile_data, null, 2) 
        : avatar.profile_data || '{}',
      avatar_image_url: avatar.avatar_image_url || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleView = async (avatar: any) => {
    try {
      const fullAvatar = await getAvatar(avatar.id);
      setSelectedAvatar(fullAvatar.avatar);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast({
        title: 'エラー',
        description: 'アバター情報の取得に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = () => {
    if (selectedAvatar) {
      try {
        const profileData = JSON.parse(formData.profile_data);
        updateMutation.mutate({ id: selectedAvatar.id, data: { ...formData, profile_data: profileData } });
      } catch (e) {
        toast({
          title: 'エラー',
          description: 'プロフィールデータの形式が正しくありません',
          variant: 'destructive',
        });
      }
    }
  };

  const handleDelete = (avatar: any) => {
    setSelectedAvatar(avatar);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedAvatar) {
      deleteMutation.mutate(selectedAvatar.id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">アバター管理</h1>
            <p className="text-gray-500 mt-2 text-base">アバター情報の管理</p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
                <Plus className="w-4 h-4 mr-1.5" />
                新規作成
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>新規アバター作成</DialogTitle>
                <DialogDescription>新しいアバターを追加します</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">名前</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="avatar_image_url">画像URL</Label>
                  <Input
                    id="avatar_image_url"
                    value={formData.avatar_image_url}
                    onChange={(e) => setFormData({ ...formData, avatar_image_url: e.target.value })}
                    placeholder="/avatar.png"
                  />
                </div>
                <div>
                  <Label htmlFor="profile_data">プロフィールデータ (JSON)</Label>
                  <textarea
                    id="profile_data"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                    rows={10}
                    value={formData.profile_data}
                    onChange={(e) => {
                      setFormData({ ...formData, profile_data: e.target.value });
                    }}
                    placeholder='{"age": 28, "gender": "男性", ...}'
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  キャンセル
                </Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '作成'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* 検索・フィルタ */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="名前または職業で検索..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-11 h-11 rounded-xl border-gray-200 bg-white"
                />
              </div>
            </div>
            <select
              className="h-11 px-4 pr-9 border border-gray-200 rounded-xl bg-white text-base font-medium text-gray-700 sm:w-48 appearance-none bg-no-repeat bg-[right_0.75rem_center]"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
              }}
              value={genderFilter}
              onChange={(e) => {
                setGenderFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">すべての性別</option>
              <option value="男性">男性</option>
              <option value="女性">女性</option>
            </select>
            <select
              className="h-11 px-4 pr-9 border border-gray-200 rounded-xl bg-white text-base font-medium text-gray-700 sm:w-48 appearance-none bg-no-repeat bg-[right_0.75rem_center]"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
              }}
              value={ageRangeFilter}
              onChange={(e) => {
                setAgeRangeFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">すべての年齢層</option>
              <option value="20-34">20-34</option>
              <option value="35-49">35-49</option>
              <option value="50+">50+</option>
            </select>
          </div>
        </div>

        {/* アバター一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">アバター一覧</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました</div>
          ) : (
            <>
              <Table className="border border-gray-200 [&_th]:border-r [&_th]:border-gray-200 [&_td]:border-r [&_td]:border-gray-200 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0">
                <TableHeader>
                  <TableRow className="border-b border-gray-200 hover:bg-transparent">
                    <TableHead className="text-base font-bold text-gray-700">ID</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">名前</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">性別</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">年齢</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">職業</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 min-w-[220px]">面接</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">作成日時</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.avatars?.map((avatar: any) => {
                    const profile = avatar.profile_data || {};
                    return (
                      <TableRow key={avatar.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{avatar.id}</TableCell>
                        <TableCell className="py-4 text-base font-medium text-gray-900">{avatar.name}</TableCell>
                        <TableCell className="py-4 text-base font-medium text-gray-900">{profile.gender || '-'}</TableCell>
                        <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{profile.age || '-'}</TableCell>
                        <TableCell className="py-4 text-base font-medium text-gray-900">{profile.occupation || '-'}</TableCell>
                        <TableCell className="py-4">
                          <div className="flex flex-col gap-1.5">
                            <button
                              type="button"
                              disabled={startInterviewMutation.isPending}
                              onClick={() => startInterviewMutation.mutate({ avatar, mode: 'standard' })}
                              title="医療面接を開始（会話画面へ）"
                              className="inline-flex h-9 w-full items-center justify-center rounded-lg border border-cyan-300 bg-cyan-50 px-3 text-base font-bold text-cyan-700 hover:bg-cyan-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Stethoscope className="w-3.5 h-3.5 mr-1 shrink-0" />
                              医療面接を開始
                            </button>
                            <button
                              type="button"
                              disabled={startInterviewMutation.isPending}
                              onClick={() => startInterviewMutation.mutate({ avatar, mode: 'intake' })}
                              title="初診時医療面接を開始（会話画面へ）"
                              className="inline-flex h-9 w-full items-center justify-center rounded-lg border border-emerald-300 bg-emerald-50 px-3 text-base font-bold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ClipboardList className="w-3.5 h-3.5 mr-1 shrink-0" />
                              初診時医療面接を開始
                            </button>
                          </div>
                        </TableCell>
                        <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">
                          {new Date(avatar.created).toLocaleString('ja-JP', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell className="py-4">
                          <div className="flex gap-2 justify-center">
                            <button
                              type="button"
                              onClick={() => handleView(avatar)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50"
                              aria-label="詳細"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEdit(avatar)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                              aria-label="編集"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(avatar)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                              aria-label="削除"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* ページネーション */}
              {data?.pagination && (
                <div className="flex justify-between items-center mt-5 pt-4">
                  <div className="text-base text-gray-700">
                    全 {data.pagination.total} 件中 {((page - 1) * 20 + 1)} - {Math.min(page * 20, data.pagination.total)} 件を表示
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      前へ
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= data.pagination.total}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      次へ
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 詳細表示ダイアログ */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>アバター詳細</DialogTitle>
            </DialogHeader>
            {selectedAvatar && (
              <div className="space-y-4">
                <div>
                  <Label>名前</Label>
                  <p className="text-sm">{selectedAvatar.name}</p>
                </div>
                <div>
                  <Label>プロフィールデータ</Label>
                  <pre className="text-xs bg-gray-100 p-4 rounded overflow-auto">
                    {JSON.stringify(selectedAvatar.profile_data || {}, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* 編集ダイアログ */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>アバター編集</DialogTitle>
              <DialogDescription>アバター情報を編集します</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">名前</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-profile_data">プロフィールデータ (JSON)</Label>
                <textarea
                  id="edit-profile_data"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                  rows={10}
                  value={formData.profile_data}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setFormData({ ...formData, profile_data: parsed });
                    } catch {
                      setFormData({ ...formData, profile_data: e.target.value });
                    }
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                キャンセル
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '更新'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>アバターを削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。アバター「{selectedAvatar?.name}」を削除しますか？
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}

